import React, { useState } from "react";
import { CALLOUT_VARIANTS, DEFAULT_BOOK_STYLE_TOKENS } from "./chapterBlocks";

const STYLE_ROWS = [
  { id: "body", label: "Body paragraph", tokenKey: "body", appliesTo: ["paragraph"] },
  { id: "chapter_title", label: "Chapter title", tokenKey: "chapter_title", appliesTo: ["chapter_title"] },
  { id: "heading_1", label: "Heading 1", tokenKey: "heading_1", appliesTo: ["heading_1"] },
  { id: "heading_2", label: "Heading 2", tokenKey: "heading_2", appliesTo: ["heading_2"] },
  { id: "quote", label: "Quote", tokenKey: "quote", appliesTo: ["quote"] },
  { id: "callout", label: "Callout", tokenKey: "callout", appliesTo: ["callout"] },
  { id: "table", label: "Table", tokenKey: "table", appliesTo: ["table"] },
];

function Field({ label, children }) {
  return (
    <label className="bww-style-field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function StyleEditor({ tokenKey, value, onChange }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });

  if (tokenKey === "callout") {
    return (
      <div className="bww-style-grid" data-testid="style-editor-callout">
        <Field label="Type">
          <select value={v.variant || "information"} onChange={(e) => set("variant", e.target.value)} data-testid="style-callout-variant">
            {CALLOUT_VARIANTS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </Field>
        <Field label="Background">
          <input type="color" value={v.background || "#eaf4fb"} onChange={(e) => set("background", e.target.value)} />
        </Field>
        <Field label="Border color">
          <input type="color" value={v.borderColor || "#4a90c8"} onChange={(e) => set("borderColor", e.target.value)} />
        </Field>
        <Field label="Border width">
          <input type="number" min={1} max={12} value={v.borderWidth ?? 4} onChange={(e) => set("borderWidth", Number(e.target.value))} />
        </Field>
        <Field label="Padding">
          <input type="number" min={0} max={48} value={v.padding ?? 14} onChange={(e) => set("padding", Number(e.target.value))} />
        </Field>
        <Field label="Corner radius">
          <input type="number" min={0} max={24} value={v.cornerRadius ?? 10} onChange={(e) => set("cornerRadius", Number(e.target.value))} />
        </Field>
      </div>
    );
  }

  if (tokenKey === "table") {
    return (
      <div className="bww-style-grid" data-testid="style-editor-table">
        <Field label="Header background">
          <input type="color" value={v.headerBackground || "#e8f6ef"} onChange={(e) => set("headerBackground", e.target.value)} />
        </Field>
        <Field label="Header text">
          <input type="color" value={v.headerTextColor || "#0f5c42"} onChange={(e) => set("headerTextColor", e.target.value)} />
        </Field>
        <Field label="Alt row">
          <input type="color" value={v.alternateRow || "#f6f6f6"} onChange={(e) => set("alternateRow", e.target.value)} />
        </Field>
        <Field label="Border color">
          <input type="color" value={v.borderColor || "#cccccc"} onChange={(e) => set("borderColor", e.target.value)} />
        </Field>
        <Field label="Cell padding">
          <input type="number" min={2} max={24} value={v.cellPadding ?? 10} onChange={(e) => set("cellPadding", Number(e.target.value))} />
        </Field>
        <Field label="Font size">
          <input type="number" min={10} max={24} value={v.fontSize ?? 14} onChange={(e) => set("fontSize", Number(e.target.value))} />
        </Field>
      </div>
    );
  }

  return (
    <div className="bww-style-grid" data-testid={`style-editor-${tokenKey}`}>
      <Field label="Font family">
        <select value={v.fontFamily || ""} onChange={(e) => set("fontFamily", e.target.value)}>
          <option value="Georgia, 'Times New Roman', serif">Georgia</option>
          <option value="Inter, system-ui, sans-serif">Inter</option>
          <option value="'Courier New', monospace">Courier</option>
          <option value="Palatino, 'Palatino Linotype', serif">Palatino</option>
        </select>
      </Field>
      <Field label="Size">
        <input type="number" min={10} max={72} value={v.fontSize ?? 18} onChange={(e) => set("fontSize", Number(e.target.value))} />
      </Field>
      {(tokenKey === "chapter_title" || tokenKey.startsWith("heading")) && (
        <Field label="Weight">
          <input type="number" min={400} max={900} step={100} value={v.fontWeight ?? 700} onChange={(e) => set("fontWeight", Number(e.target.value))} />
        </Field>
      )}
      {tokenKey === "quote" && (
        <Field label="Font style">
          <select value={v.fontStyle || "italic"} onChange={(e) => set("fontStyle", e.target.value)}>
            <option value="italic">Italic</option>
            <option value="normal">Normal</option>
          </select>
        </Field>
      )}
      {tokenKey === "body" && (
        <>
          <Field label="Line height">
            <input type="number" min={1} max={3} step={0.05} value={v.lineHeight ?? 1.75} onChange={(e) => set("lineHeight", Number(e.target.value))} />
          </Field>
          <Field label="Paragraph spacing">
            <input type="number" min={0} max={48} value={v.paragraphSpacing ?? 18} onChange={(e) => set("paragraphSpacing", Number(e.target.value))} />
          </Field>
          <Field label="First-line indent">
            <input type="number" min={0} max={64} value={v.firstLineIndent ?? 0} onChange={(e) => set("firstLineIndent", Number(e.target.value))} />
          </Field>
        </>
      )}
      <Field label="Color">
        <input type="color" value={v.color || "#172019"} onChange={(e) => set("color", e.target.value)} />
      </Field>
      <Field label="Alignment">
        <select value={v.alignment || "left"} onChange={(e) => set("alignment", e.target.value)}>
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
          <option value="justify">Justify</option>
        </select>
      </Field>
      {(tokenKey === "chapter_title" || tokenKey.startsWith("heading") || tokenKey === "quote") && (
        <>
          <Field label="Spacing above">
            <input type="number" min={0} max={80} value={v.spacingAbove ?? 0} onChange={(e) => set("spacingAbove", Number(e.target.value))} />
          </Field>
          <Field label="Spacing below">
            <input type="number" min={0} max={80} value={v.spacingBelow ?? 12} onChange={(e) => set("spacingBelow", Number(e.target.value))} />
          </Field>
        </>
      )}
      {tokenKey === "quote" && (
        <>
          <Field label="Border color">
            <input type="color" value={v.borderColor || "#43d895"} onChange={(e) => set("borderColor", e.target.value)} />
          </Field>
          <Field label="Background">
            <input type="color" value={v.background || "#f3faf6"} onChange={(e) => set("background", e.target.value)} />
          </Field>
          <Field label="Padding">
            <input type="number" min={0} max={48} value={v.padding ?? 16} onChange={(e) => set("padding", Number(e.target.value))} />
          </Field>
        </>
      )}
    </div>
  );
}

export default function StylesAssistantPanel({
  bookTokens,
  draftTokens,
  onDraftChange,
  selectedBlock,
  busy,
  onApplySelected,
  onApplyChapter,
  onApplyBook,
  onReset,
  onCopyChapterStyle,
  onPasteChapterStyle,
  canPaste,
  onSavePreset,
  headerConfig,
  footerConfig,
  onHeaderFooterChange,
  onSaveHeaderFooter,
}) {
  const [openRow, setOpenRow] = useState("body");
  const tokens = draftTokens || bookTokens || DEFAULT_BOOK_STYLE_TOKENS;

  return (
    <section className="bww-right-section" data-testid="bl-styles-panel">
      <h3>Book styles</h3>
      <p>
        {selectedBlock
          ? `Selected: ${selectedBlock.type.replace(/_/g, " ")}`
          : "Select a block on the page, or edit book style tokens below."}
      </p>
      <div className="bww-action-list">
        {STYLE_ROWS.map((row) => {
          const open = openRow === row.id;
          return (
            <div key={row.id} className="bww-style-row">
              <button
                type="button"
                className={`bww-assistant-action${open ? " active" : ""}`}
                data-testid={`bl-style-row-${row.id}`}
                onClick={() => setOpenRow(open ? null : row.id)}
              >
                {row.label}
              </button>
              {open && (
                <div className="bww-style-drawer">
                  <StyleEditor
                    tokenKey={row.tokenKey}
                    value={tokens[row.tokenKey]}
                    onChange={(next) => onDraftChange({ ...tokens, [row.tokenKey]: next })}
                  />
                  <div className="bww-style-apply-row">
                    <button type="button" className="bww-btn" data-testid={`bl-style-apply-selected-${row.id}`}
                      disabled={!selectedBlock || busy} onClick={() => onApplySelected(row.tokenKey, tokens[row.tokenKey], row.appliesTo)}>
                      Apply to selected block
                    </button>
                    <button type="button" className="bww-btn" data-testid={`bl-style-apply-chapter-${row.id}`}
                      disabled={busy} onClick={() => onApplyChapter(row.tokenKey, tokens[row.tokenKey], row.appliesTo)}>
                      Apply to this chapter
                    </button>
                    <button type="button" className="bww-btn" data-testid={`bl-style-apply-book-${row.id}`}
                      disabled={busy} onClick={() => onApplyBook(row.tokenKey, tokens[row.tokenKey])}>
                      Apply to entire book
                    </button>
                    <button type="button" className="bww-btn" data-testid={`bl-style-reset-${row.id}`}
                      disabled={busy} onClick={() => onReset(row.tokenKey)}>
                      Reset to book style
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="bww-action-list" style={{ marginTop: 12 }}>
        <button type="button" className="bww-assistant-action" data-testid="bl-style-copy-chapter" onClick={onCopyChapterStyle}>
          Copy chapter style
        </button>
        <button type="button" className="bww-assistant-action" data-testid="bl-style-paste-chapter"
          disabled={!canPaste} title={canPaste ? "Paste copied chapter styles" : "Copy a chapter style first"}
          onClick={onPasteChapterStyle}>
          Paste chapter style
        </button>
        <button type="button" className="bww-assistant-action" data-testid="bl-style-save-preset" disabled={busy} onClick={onSavePreset}>
          Save as book style preset
        </button>
      </div>

      <div className="bww-style-drawer" style={{ marginTop: 14 }} data-testid="bl-header-footer-styles">
        <h4 style={{ margin: "0 0 8px", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em" }}>Headers &amp; footers</h4>
        <p style={{ marginTop: 0 }}>Stored in Book Styles — not affected by Paper/Dark page view.</p>
        <div className="bww-style-grid">
          <label className="bww-style-field">
            <span>Header left</span>
            <input data-testid="bl-header-left" value={headerConfig?.left || ""} onChange={(e) => onHeaderFooterChange({ header: { ...(headerConfig || {}), left: e.target.value, enabled: true } })} />
          </label>
          <label className="bww-style-field">
            <span>Header right</span>
            <input data-testid="bl-header-right" value={headerConfig?.right || ""} onChange={(e) => onHeaderFooterChange({ header: { ...(headerConfig || {}), right: e.target.value, enabled: true } })} />
          </label>
          <label className="bww-style-field">
            <span>Footer text</span>
            <input data-testid="bl-footer-text" value={footerConfig?.text || ""} onChange={(e) => onHeaderFooterChange({ footer: { ...(footerConfig || {}), text: e.target.value, enabled: true } })} />
          </label>
        </div>
        <button type="button" className="bww-btn" style={{ marginTop: 8 }} data-testid="bl-save-header-footer" disabled={busy} onClick={onSaveHeaderFooter}>
          Save header / footer to book
        </button>
      </div>
    </section>
  );
}
